import {
  Button,
  Link as MuiLink,
  Stack,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { environment } from '@env';

import { GeocodeAPIV6Response } from '@/types/mapbox';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetAllBuildings } from '@/api/endpoints/building/useGetAllBuildings';
import {
  ApiResponse_GetAll_PropertyLocation,
  useGetAllPropertyLocations,
} from '@/api/endpoints/propertyLocation/useGetAllPropertyLocations';
import { useGetAllUnitsInBuilding } from '@/api/endpoints/unit/useGetAllUnitsInBuilding';
import { PropertyLocationDeclaredUnitsCount } from '@/api/enums';
import { PropertyLocationModel } from '@/api/models/propertyLocation';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatPhoneNumber } from '@/utils/phone';
import {
  getPropertyTypeDisplayText,
  isSingleFamilyEquivalentType,
} from '@/utils/propertyLocationTypeUtils';
import { getResolvedRoutePath } from '@/utils/router';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import PropertyLocationCreateModal from '@/components/propertyLocation/PropertyLocationCreateModal';
import PropertyLocationUpdateModal from '@/components/propertyLocation/PropertyLocationUpdateModal';

const TABLE_HEAD = [
  { id: 'name', label: 'Property Name', sortable: true, width: '25%' },
  { id: 'email', label: 'Property Email', sortable: false, width: '20%' },
  { id: 'phone', label: 'Property Phone', sortable: false, width: '20%' },
  { id: 'type', label: 'Property Type', sortable: true, width: '15%' },
  { id: 'units', label: 'Units', sortable: false, width: '10%' },
];

type Order = 'asc' | 'desc';
type PropertyLocation = ApiResponse_GetAll_PropertyLocation[0];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'name' || orderBy === 'type') {
    const aValue = String(a[orderBy]).toLowerCase();
    const bValue = String(b[orderBy]).toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

const PropertyLocationListPage: React.FC = () => {
  const { session } = useAuth();
  const { isSingleFamilyAccount } = usePropertyManager();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof PropertyLocation>('name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [propertyLocationToUpdate, setPropertyLocationToUpdate] =
    useState<PropertyLocationModel | null>(null);

  const getAllPropertyLocationsQuery = useGetAllPropertyLocations();

  const getAllBuildingsQuery = useGetAllBuildings(
    {
      location_id: getAllPropertyLocationsQuery.data?.[0]?.id,
    },
    isSingleFamilyAccount && getAllPropertyLocationsQuery.data?.length === 1
  );

  const getAllUnitsQuery = useGetAllUnitsInBuilding(
    {
      buildingId: getAllBuildingsQuery.data?.[0]?.id,
    },
    isSingleFamilyAccount && getAllBuildingsQuery.data?.length === 1
  );

  const geocodeLocationsQuery = useQuery({
    enabled: !!getAllPropertyLocationsQuery.data?.length,
    queryKey: ['GET', 'geocode', getAllPropertyLocationsQuery.data?.map(loc => loc.address_text)],
    queryFn: async () => {
      if (!getAllPropertyLocationsQuery.data) {
        return [];
      }

      const geocodedLocations = await Promise.all(
        getAllPropertyLocationsQuery.data.map(async location => {
          try {
            const response = await axios.get<GeocodeAPIV6Response>(
              'https://api.mapbox.com/search/geocode/v6/forward?',
              {
                params: new URLSearchParams({
                  q: location.address_text,
                  access_token: environment.mapboxApiKey,
                  autocomplete: 'false',
                  country: 'US',
                  format: 'geojson',
                  language: 'en-US',
                  limit: '1',
                  types: 'address',
                }),
              }
            );
            return {
              id: location.id,
              coordinates: response.data.features[0]?.geometry?.coordinates as
                | [number, number]
                | null,
            };
          } catch (error) {
            return {
              id: location.id,
              coordinates: null,
            };
          }
        })
      );
      return geocodedLocations;
    },
  });

  const [downloadProgress, setDownloadProgress] = useState<number>(0);

  const downloadDocumentsMutation = useMutation({
    mutationFn: async (propertyLocation: PropertyLocationModel) => {
      setDownloadProgress(0);

      const response = await axios.get(
        `${environment.api}/document/location/${propertyLocation.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
          responseType: 'blob',
          timeout: 300000, // 5 minutes timeout
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onDownloadProgress: progressEvent => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setDownloadProgress(progress);
            } else {
              // If total is unknown, show indeterminate progress
              setDownloadProgress(-1);
            }
          },
        }
      );

      if (!response.data || response.data.size === 0) {
        throw new Error('Empty response received');
      }

      if (!(response.data instanceof Blob)) {
        throw new Error('Response is not a blob');
      }

      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('zip')) {
        throw new Error(`Invalid content type received: ${contentType}`);
      }

      const blob = new Blob([response.data], { type: contentType });

      // Get filename from content-disposition header or generate default
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
      const filename = filenameMatch
        ? filenameMatch[1]
        : `location_${propertyLocation.id}_documents.zip`;

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    },
    onSuccess: () => {
      setDownloadProgress(0);
      enqueueSnackbar('Documents downloaded successfully.', { variant: 'success' });
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError) {
        if (error.code === 'ECONNABORTED') {
          enqueueSnackbar('Download timed out. Please try again.', { variant: 'error' });
        } else if (error.response?.status === 404) {
          enqueueSnackbar('No documents found for this location.', { variant: 'warning' });
        } else if (error.response?.status === 403) {
          enqueueSnackbar('You do not have permission to download these documents.', {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(`Failed to download location documents: ${error.message}`, {
            variant: 'error',
          });
        }
      } else if (error instanceof Error) {
        enqueueSnackbar(`Download failed: ${error.message}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to download location documents.', { variant: 'error' });
      }
      setDownloadProgress(0);
    },
  });

  const onDownloadDocuments = useCallback(
    (propertyLocation: PropertyLocationModel) =>
      downloadDocumentsMutation.mutateAsync(propertyLocation),
    [downloadDocumentsMutation]
  );

  const isLoading =
    getAllPropertyLocationsQuery.isLoading ||
    getAllBuildingsQuery.isLoading ||
    getAllUnitsQuery.isLoading ||
    geocodeLocationsQuery.isLoading;

  const handleRequestSort = (property: keyof PropertyLocation) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllPropertyLocationsQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllPropertyLocationsQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        flex: '1 1 100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          display: 'flex',
          justifyContent: { sm: 'space-between' },
          width: '100%',
        }}
      >
        <DashboardHeader hasInnerHtml title="My Properties: <em>Locations</em>" />
        <Button
          color="primary"
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<PlusIcon />}
          component={RouterLink}
          to={PMPRoutes.propertyLocationCreate}
        >
          Add Location
        </Button>
      </Box>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 2,
          mt: { xs: 1, sm: 0 },
        }}
      >
        These are the properties you're currently managing on the platform. Whenever you have a new
        rental property, you can add it as a new location using the "Add Location" button.
      </Typography>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search properties..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === '' ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof PropertyLocation)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    width: '50px',
                    bgcolor: theme.palette.secondary[200],
                    borderRight: 'none',
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No locations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => {
                  const locationId = Number(row.id);
                  const linkPath = getResolvedRoutePath(PMPRoutes.propertyLocationView, {
                    id: String(locationId),
                  });

                  return (
                    <TableRow
                      key={String(row.id)}
                      sx={{
                        '&:hover': {
                          backgroundColor: `${theme.palette.secondary[200]} !important`,
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        },
                      }}
                    >
                      <TableCell sx={{ width: '25%' }}>
                        <MuiLink
                          component={RouterLink}
                          to={linkPath}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {row.name}
                        </MuiLink>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        {row.email?.value || 'Contact Terr.ai'}
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        {row.phone?.value ? formatPhoneNumber(row.phone.value) : 'Contact Terr.ai'}
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        {getPropertyTypeDisplayText(row.type)}
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        {isSingleFamilyEquivalentType(row.type) ? (
                          '1'
                        ) : (
                          <>
                            {row.buildings?.reduce(
                              (acc, building) => acc + building._count.units,
                              0
                            ) || 0}{' '}
                            {(() => {
                              switch (row.declared_units_count) {
                              case PropertyLocationDeclaredUnitsCount.FROM_1_TO_5:
                                return '(1-5)';
                              case PropertyLocationDeclaredUnitsCount.FROM_6_TO_25:
                                return '(6-25)';
                              case PropertyLocationDeclaredUnitsCount.FROM_26_TO_100:
                                return '(26-100)';
                              case PropertyLocationDeclaredUnitsCount.FROM_101_TO_500:
                                return '(101-500)';
                              case PropertyLocationDeclaredUnitsCount.ABOVE_500:
                                return '(500+)';
                              default:
                                return '';
                              }
                            })()}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: '50px' }}>
                        <RowActionsMenu
                          items={[
                            {
                              label: 'Edit',
                              onClick: () => {
                                setPropertyLocationToUpdate(row);
                                setIsUpdateModalOpen(true);
                              },
                            },
                            {
                              label: 'Download Documents',
                              onClick: () => onDownloadDocuments(row),
                              loadingProgress: downloadProgress,
                              isLoading:
                                downloadDocumentsMutation.isPending &&
                                downloadDocumentsMutation.variables?.id === row.id,
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isCreateModalOpen && (
        <PropertyLocationCreateModal
          onClose={() => setIsCreateModalOpen(false)}
          onPropertyLocationCreated={() => setIsCreateModalOpen(false)}
        />
      )}

      {isUpdateModalOpen && !!propertyLocationToUpdate && (
        <PropertyLocationUpdateModal
          propertyLocation={propertyLocationToUpdate}
          onClose={() => {
            setIsUpdateModalOpen(false);
            setPropertyLocationToUpdate(null);
          }}
        />
      )}
    </Stack>
  );
};

export default PropertyLocationListPage;
